import { toast } from 'react-toastify';

import { updateLocalURL } from '@/services/urlParams';

import api from '../services/api';
import { store } from '../store';
import { IComboSettings } from '../store/ducks/combos/types';
import {
  setBestSellers,
  setCategory,
  setClientOffers,
  setFreeShipping,
  setGiftClub,
  setGroup,
  setOffers,
  setRandomOffers,
  setSuperOffers,
} from '../store/ducks/productLists/actions';
import * as ProductsActions from '../store/ducks/products/actions';
import { IProduct, IProductsGroupByCategory } from '../store/ducks/products/types';
import * as SearchesActions from '../store/ducks/searches/actions';
import { ISearch } from '../store/ducks/searches/types';

export interface ISecondaryImages {
  id: number;
  path: string;
  product_id: number;
  created_at: string;
  updated_at: string;
}

const { dispatch } = store;

export const getProductsByCategoryId = async (shopId: number, categoryId: number) => {
  dispatch(ProductsActions.loadRequest());

  return api
    .get<{ category_id: number }, IProductsGroupByCategory[]>(`/shops/${shopId}/products`, {
      params: { category_id: categoryId },
    })
    .then((productsGroupByCategory) =>
      dispatch(ProductsActions.loadSuccess(productsGroupByCategory)),
    )
    .catch((err) => {
      console.error(err);
      dispatch(ProductsActions.loadFailure());
    });
};

export const getClientOffers = async (
  clientId: number,
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
) => {
  dispatch(setClientOffers({ error: false, loading: true, products: [] }));

  return api
    .get<{ shop_id: number; limit: number }, IProduct[]>(`/clients/${clientId}/client-promo`, {
      params: { limit, shop_id: shopId, show_striped_medicine_products },
    })
    .then((products) => dispatch(setClientOffers({ error: false, loading: false, products })))
    .catch(() => dispatch(setClientOffers({ error: true, loading: false, products: [] })));
};

export const getProductsInOffer = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
) => {
  dispatch(setOffers({ error: false, loading: true, products: [] }));

  return api
    .get<{ limit: number }, IProduct[]>(`/shops/${shopId}/promo-products`, {
      params: { limit, show_striped_medicine_products },
    })
    .then((products) => {
      dispatch(setOffers({ error: false, loading: false, products }));
    })
    .catch(() => dispatch(setOffers({ error: false, loading: false, products: [] })));
};

export const getProduct = async (
  shopId: number,
  productId: number,
): Promise<{ fullProducts: IProduct[]; specProducts: IProduct[] }> => {
  dispatch(ProductsActions.setProductLoading());

  return api
    .get<void, IProduct[]>(`shops/${shopId}/products/${productId}`)
    .then((fullProducts) => {
      fullProducts.forEach((fp) => {
        fp.options_group?.options.sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
      });

      const specProducts = fullProducts
        .filter((fp) => fp.data && fp.data.spec)
        .sort((a, b) => (a.data?.spec?.color! > b.data?.spec?.color! ? 1 : -1));

      dispatch(ProductsActions.setSpecProducts(specProducts));

      return { fullProducts, specProducts };
    })
    .catch(() => {
      return { fullProducts: [], specProducts: [] };
    });
};

export const getBestSellers = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
) => {
  dispatch(setBestSellers({ loading: true, error: false, products: [] }));

  return api
    .get<{ limit: number }, IProduct[]>(`shops/${shopId}/top-products`, {
      params: { limit, show_striped_medicine_products },
    })
    .then((products) => dispatch(setBestSellers({ loading: false, error: false, products })))
    .catch(() => dispatch(setBestSellers({ loading: false, error: true, products: [] })));
};

export const smartSearch = async ({
  shopId,
  title,
  brands,
  category_ids,
  group_ids,
  min_price,
  max_price,
  sizes,
}: {
  shopId: number;
  title: string;
  brands?: string[];
  category_ids?: number[];
  group_ids?: number[];
  min_price?: number;
  max_price?: number;
  sizes?: string[];
}) => {
  dispatch(SearchesActions.loadRequest());
  dispatch(SearchesActions.setTitleSearched(title));

  return api
    .get<{ shop_id: number; title: string }, ISearch>(`/products/search`, {
      params: {
        shop_id: shopId,
        title,
        brands,
        category_ids,
        group_ids,
        min_price,
        max_price,
        sizes,
      },
    })

    .then((searched) => {
      const paramMap: { [key: string]: any } = {
        category_ids,
        brands,
      };
      updateLocalURL(paramMap);

      dispatch(SearchesActions.loadSuccess(searched));
    })
    .catch((err) => {
      dispatch(SearchesActions.loadFailure());
      console.error(err);
    });
};

export const getSecondaryImages = async (shopId: number, productId: number) => {
  return api.get<void, ISecondaryImages[]>(
    `/shops/${shopId}/products/${productId}/get-secondary-images`,
  );
};

export const getRandomProducts = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
) => {
  dispatch(setRandomOffers({ error: false, loading: true, products: [] }));

  return api
    .get<{ shopId: number; limit?: number }, IProduct[]>('/products/get-random-products', {
      params: { shop_id: shopId, limit, show_striped_medicine_products },
    })
    .then((products) => dispatch(setRandomOffers({ error: false, loading: false, products })))
    .catch(() => dispatch(setRandomOffers({ error: true, loading: false, products: [] })));
};

export const getCategoryProducts = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
  category_id: number,
) => {
  const ProductsLists = store.getState().productLists;
  dispatch(
    setCategory({
      error: { ...ProductsLists.category.error, [category_id]: false },
      loading: { ...ProductsLists.category.loading, [category_id]: true },
      products: { ...ProductsLists.category.products },
    }),
  );

  return api
    .get<{ shopId: number; limit?: number }, IProduct[]>('/products/get-random-products', {
      params: { shop_id: shopId, limit, category_id, show_striped_medicine_products },
    })
    .then((products) => {
      const ProductsLists = store.getState().productLists;
      const prevProducts = ProductsLists.category.products || {};
      const prevError = ProductsLists.category.error || {};
      const prevLoading = ProductsLists.category.loading || {};
      dispatch(
        setCategory({
          error: { ...prevError, [category_id]: false },
          loading: { ...prevLoading, [category_id]: false },
          products: { ...prevProducts, [category_id]: products },
        }),
      );
    })
    .catch(() => {
      const ProductsLists = store.getState().productLists;
      dispatch(
        setCategory({
          error: { ...ProductsLists.category.error, [category_id]: true },
          loading: { ...ProductsLists.category.loading, [category_id]: false },
          products: { ...ProductsLists.category.products, [category_id]: [] },
        }),
      );
    });
};

export const getGroupProducts = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
  group_id: number,
) => {
  const ProductsLists = store.getState().productLists;
  dispatch(
    setGroup({
      error: { ...ProductsLists.group.error, [group_id]: false },
      loading: { ...ProductsLists.group.loading, [group_id]: true },
      products: { ...ProductsLists.group.products },
    }),
  );

  return api
    .get<{ shopId: number; limit?: number }, IProduct[]>('/products/get-random-products', {
      params: { shop_id: shopId, limit, group_id, show_striped_medicine_products },
    })
    .then((products) => {
      const ProductsLists = store.getState().productLists;
      const prevProducts = ProductsLists.group.products || {};
      const prevError = ProductsLists.group.error || {};
      const prevLoading = ProductsLists.group.loading || {};
      dispatch(
        setGroup({
          error: { ...prevError, [group_id]: false },
          loading: { ...prevLoading, [group_id]: false },
          products: { ...prevProducts, [group_id]: products },
        }),
      );
    })
    .catch(() => {
      const ProductsLists = store.getState().productLists;
      dispatch(
        setGroup({
          error: { ...ProductsLists.group.error, [group_id]: true },
          loading: { ...ProductsLists.group.loading, [group_id]: false },
          products: { ...ProductsLists.group.products, [group_id]: [] },
        }),
      );
    });
};

export const getGiftClubProducts = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
  worldId?: number,
) => {
  dispatch(setGiftClub({ error: false, loading: true, products: [] }));

  return api
    .get<{ shopId: number; limit?: number; world_id: number }, IProduct[]>('/products/gift-club', {
      params: { shop_id: shopId, limit, world_id: worldId, show_striped_medicine_products },
    })
    .then((products) => dispatch(setGiftClub({ error: false, loading: false, products })))
    .catch(() => dispatch(setGiftClub({ error: true, loading: false, products: [] })));
};

export const getSuperOffersProducts = async (
  shopId: number,
  limit: number,
  show_striped_medicine_products: number,
  worldId?: number,
) => {
  dispatch(setSuperOffers({ error: false, loading: true, products: [] }));

  return api
    .get<{ shopId: number; limit?: number; world_id: number }, IProduct[]>(
      '/products/super-offers',
      {
        params: { shop_id: shopId, limit, world_id: worldId, show_striped_medicine_products },
      },
    )
    .then((products) => dispatch(setSuperOffers({ error: false, loading: false, products })))
    .catch(() => dispatch(setSuperOffers({ error: true, loading: false, products: [] })));
};

export const getFreeShippingProducts = async (
  shopId: number,
  limit = 30,
  show_striped_medicine_products?: number,
) => {
  dispatch(setFreeShipping({ error: false, loading: true, products: [] }));

  return api
    .get<{ shopId: number; limit?: number }, IProduct[]>('/products/free-delivery-products', {
      params: { shop_id: shopId, limit, show_striped_medicine_products },
    })
    .then((products) => dispatch(setFreeShipping({ error: false, loading: false, products })))
    .catch(() => dispatch(setFreeShipping({ error: true, loading: false, products: [] })));
};

export const getProductByCombo = async (
  shopId: number | undefined,
  comboId: number | undefined,
) => {
  return api
    .get<void, IProduct[]>(`shops/${shopId}/combos/${comboId}/products`)
    .then((response) => response)
    .catch((err) => {
      console.error(err);
    });
};

export const applyComboPromotion = async (
  orderId: number | undefined,
  comboSettings: IComboSettings,
) => {
  return api
    .post(`orders/${orderId}/combos/apply`, { ...comboSettings })
    .then((response) => response)
    .catch((err) => {
      console.error('applyComboPromotion error:', err);

      const data = err?.response?.data;

      if (data) {
        const message = typeof data === 'string' ? data : data.message ?? JSON.stringify(data);
        const type = (data.type as any) ?? 'warning';

        toast(message, { type });

        throw err;
      }

      throw err;
    });
};

export const updateComboPromotion = async (
  orderId: number | undefined,
  comboId: number,
  combo_seq_id: number | undefined,
  comboSettings: { full_products: { id: number; quantity: number }[] },
) => {
  return api
    .put(`orders/${orderId}/combos/${comboId}/combo-seq/${combo_seq_id}`, { ...comboSettings })
    .then((response) => response)
    .catch((err) => {
      console.error('updateComboPromotion error:', err);

      const data = err?.response?.data;

      if (data) {
        const message = typeof data === 'string' ? data : data.message ?? JSON.stringify(data);
        const type = (data.type as any) ?? 'warning';

        toast(message, { type });

        throw err;
      }

      throw err;
    });
};

export const removeComboPromotion = async (
  orderId: number | undefined,
  comboId: number,
  combo_seq_id: number | undefined,
) => {
  return api
    .delete(`orders/${orderId}/combos/${comboId}/combo-seq/${combo_seq_id}`)
    .then((response) => response)
    .catch((err) => {
      console.error('removeComboPromotion error:', err);

      const data = err?.response?.data;

      if (data) {
        const message = typeof data === 'string' ? data : data.message ?? JSON.stringify(data);
        const type = (data.type as any) ?? 'warning';

        toast(message, { type });

        throw err;
      }

      throw err;
    });
};

export const getAvailableCombos = async (orderId: number | undefined) => {
  return api
    .get<void, IComboSettings>(`/orders/${orderId}/combos/available-combos`)
    .then((response) => response)
    .catch((err) => {
      console.error('getAvailableCombos error:', err);
    });
};

export const getCombo = async (comboId: number | undefined) => {
  return api.get(`combos/${comboId}`);
};
